<template>
  <div class="root border-left" ref="viewport">
    <div id="above">
      <b-button class="d-none d-lg-block float-left ml-3 mt-3" variant="light" title="Toggle watercolumn sidebar" @click="toggleWatercolumnSidebar">
        <font-awesome-icon :icon="['fas', collapseToggleBtnIcon]" />
      </b-button>
    </div>
    <div id="watercolumn" style="position: relative;">
      <div v-for="(platform, name, index) in this.divingPlatforms" :ref="'platform' + platform.id" class="platform" :key="platform.id"
           :style="{ position: 'absolute', top: platformDepthPixels(platform.id) + 'px', left: platformOffsetPixels(platform.id, index) + 'px' }"
        :class="showPlatform(platform.id)">
        <div style="text-align: center;" :style="{ width: platformWidthPixels + 'px' }">
          <div style="position: absolute; left: 50%;" :style="{ top: '-20px' }">
            <div class="platformLabel">{{ platform.name }}</div>
          </div>
          <img class="platformIcon" :ref="'platformIcon' + platform.id" :src="platform.watercolumn.icon.url" :alt="'Icon of ' + platform.name">
          <span class="platformDepthLabel" v-if="depth(platform.id) >= 0">{{ showDigits(depth(platform.id), digitsDepthLabel(platform.id)) }}</span>
        </div>
        <div v-if="altitude(platform.id) > 0">
          <div class="altitude" v-if="platform.type === 'auv-g500' || (platform.type === 'transponder' && dive_stage(platform.id) === 'SEAFLOOR')"
               :style="{ height: platformAltitudePixels(platform.id) + 'px', width: platformWidthPixels + 'px' }">
            <div style="position: absolute;" :style="{ left: platformAltitudeLabelLeft(platform.type) } ">
              <div class="altitudeLabel"><strong>{{ showDigits(altitude(platform.id), 1) }}</strong></div>
            </div>
            <svg :height="platformAltitudePixels(platform.id)" :width="platformWidthPixels">
              <polygon :points="platformAltitudeSymbol(platform.id)" :style="platformAltitudeStyle(platform.id)" />
            </svg>
          </div>
          <div class="seafloor" v-if="platform.type === 'auv-g500' || (platform.type === 'transponder' && dive_stage(platform.id) === 'SEAFLOOR')" style="width: 100%">
            <span class="seafloorDepthLabel">{{ showDigits(depth(platform.id) + altitude(platform.id), 1) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters, mapMutations, mapState } from 'vuex'
import Helpers from '@/mixins/helpers'

const NAVIGATION = 1
const OPERATION = 4

const PLATFORM_WIDTH_PIXEL = 72

export default {
  name: 'WatercolumnWidget',
  mixins: [
    Helpers
  ],
  data () {
    return {
      viewPort: {
        width: 0,
        height: 0
      },
      depthMax: 0,
      depthFactor: 0,
      heightAbove: 0,
      heightWatercolumn: 0
    }
  },
  created: function () {
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'add_platform') {
        if (!(mutation.payload.id in this.divingPlatforms)) {
          this.addPlatform(Number(mutation.payload.id))
        }
      }
    })
  },
  beforeDestroy: function () {
    // Unsubscribe from state mutations
    this.unsubscribe()
  },
  computed: {
    ...mapState(['platforms', 'collapseStates', 'selectedPlatform']),
    ...mapGetters(['currentUpdate', 'campaignSelected']),
    showPlatform: function () {
      return (platformId) => {
        if (this.collapseStates['sidebar-watercolumn'] && platformId !== this.selectedPlatform) {
          return 'd-none'
        }
        return 'd-block'
      }
    },
    digitsDepthLabel: function () {
      return (platformId) => {
        if (this.depth(platformId) < 10) {
          return 1
        }
        return 0
      }
    },
    collapseToggleBtnIcon: function () {
      if (this.collapseStates['sidebar-watercolumn']) {
        return 'angle-double-left'
      }
      return 'angle-double-right'
    },
    divingPlatforms: function () {
      const divingPlatforms = {}
      for (const platformId in this.platforms) {
        const platform = this.platforms[platformId]
        if ((platform.type.toLowerCase() === 'auv-g500' ||
          platform.type.toLowerCase() === 'transponder') &&
          'watercolumn' in platform && 'last_change' in platform &&
          this.campaignSelected.platforms.includes(Number(platformId))) {
          divingPlatforms[platformId] = platform
        }
      }
      return divingPlatforms
    },
    depth: function () {
      return (platformId) => {
        const update = this.currentUpdate(platformId, NAVIGATION, 4)
        if (update.length !== 0) {
          const seafloorDepth = Number(update[1]) + this.altitude(platformId)
          if (seafloorDepth > this.depthMax) {
            this.depthMax = seafloorDepth
            this.refreshViewport()
          }
          return Number(update[1])
        }
        return 0
      }
    },
    altitude: function () {
      return (platformId) => {
        const update = this.currentUpdate(platformId, NAVIGATION, 5)
        return (update.length !== 0) ? Number(update[1]) : 0
      }
    },
    dive_stage: function () {
      return (platformId) => {
        const update = this.currentUpdate(platformId, OPERATION, 1)
        return (update.length !== 0) ? update[1] : 'UNKNOWN'
      }
    },
    platformDepthPixels: function () {
      return (platformId) => {
        return Math.round((this.depth(platformId) * this.depthFactor) - (this.platforms[platformId].watercolumn.icon.height * 0.67))
      }
    },
    platformAltitudePixels: function () {
      return (platformId) => {
        return (this.altitude(platformId) * this.depthFactor)
      }
    },
    platformWidthPixels: function () {
      return PLATFORM_WIDTH_PIXEL
    },
    platformOffsetPixels: function () {
      return (platformId, index) => {
        if (!this.collapseStates['sidebar-watercolumn']) {
          return this.platformWidthPixels * index
        }
        return 0
      }
    },
    platformAltitudeSymbol: function () {
      return (platformId) => {
        const height = this.platformAltitudePixels(platformId)
        const width = this.platformWidthPixels
        const vmargin = 1
        if (this.platforms[platformId].type === 'auv-g500') {
          const hmargin = 15
          return Number(width / 2) + ',0 ' +
            Number(width / 2 - hmargin) + ',' + Number(height - vmargin) + ' ' +
            Number(width / 2 + hmargin) + ',' + Number(height - vmargin)
        } else if (this.platforms[platformId].type === 'transponder') {
          const offset = Number(5 + (this.getNumberofDigits(this.depth(platformId)) - 1) * 4)
          return Number((width / 2) - offset) + ',0 ' + Number((width / 2) - offset) + ',' + Number(height)
        }
      }
    },
    platformAltitudeStyle: function () {
      return (platformId) => {
        if (this.platforms[platformId].type === 'auv-g500') {
          return 'fill:white;stroke:white;stroke-width:1;fill-opacity:0.2;opacity:0.5'
        } else if (this.platforms[platformId].type === 'transponder') {
          return 'fill:tan;stroke:tan;stroke-width:2;opacity:1.0'
        }
      }
    },
    platformAltitudeLabelLeft: function () {
      return (platformType) => {
        if (platformType === 'auv-g500') {
          return '50%'
        } else if (platformType === 'transponder') {
          return '60%'
        }
      }
    }
  },
  methods: {
    ...mapMutations(['toggle_collapse']),
    toggleWatercolumnSidebar: function () {
      this.toggle_collapse('sidebar-watercolumn')
    },
    readIconDimensions: function (platformId) {
      const icon = this.platforms[platformId].watercolumn.icon
      const img = new Image()
      img.onerror = () => {
        console.error('watercolumn: icon not found in ' + icon.url)
      }
      img.onload = () => {
        icon.width = img.width
        icon.height = img.height
      }
      img.src = icon.url
    },
    refreshViewport: function () {
      if (this.$refs.viewport) {
        this.viewPort.width = this.$refs.viewport.offsetWidth
        this.viewPort.height = this.$refs.viewport.offsetHeight
        // Recalculate
        this.heightAbove = 180
        this.heightWatercolumn = this.viewPort.height - this.heightAbove
        this.depthFactor = Math.round((this.heightWatercolumn) / this.depthMax)
      }
    },
    addPlatform: function (platformId) {
      const platform = this.platforms[platformId]
      if (platform.type !== 'auv-g500' && platform.type !== 'transponder') {
        return
      }
      console.debug('watercolumn: setting up platform ' + platform.id)
      platform.watercolumn = {
        icon: {
          url: platform.icon_url,
          width: 0,
          height: 0
        }
      }
      Vue.set(this.platforms, Number(platform.id), platform)
      // Read icon dimensions
      this.readIconDimensions(platform.id)
      // Reload watercolumn display
      this.refreshViewport()
    }
  }
}
</script>

<style scoped>

.root {
  height: 100%;
  width: 100%;
}

#above {
  background-color: rgb(199, 242, 255);
  height: 100px;
}

#watercolumn {
  height: 100%;
  background: linear-gradient(0deg, rgb(0, 25, 32), rgb(0, 148, 191));
  border-top: 2px solid rgb(0, 94, 130);
  display: block;
}

.platformLabel {
  position: relative;
  left: -50%;
  background: rgba(255, 255, 255, 0.75);
  padding: 0 3px 0 3px;
  border-radius: 3px;
  font-size: 11px;
}

.platformDepthLabel {
  margin-left: 3px;
  font-size: 13px;
  font-weight: bold;
  vertical-align: middle;
  text-align: center;
  color: #ffffff;
}

.altitude {
  position: relative;
  display: flex;
  align-items: center;
}

.altitudeLabel {
  position: relative;
  left: -50%;
  padding: 1px 2px 1px 2px;
  border-radius: 3px;
  font-size: 13px;
  color: #ffffff;
}

.seafloor {
  height: 1000px;
  text-align: center;
  border-top: 3px solid rgb(163, 121, 0);
  background: linear-gradient(0deg, rgb(200, 148, 0), rgb(255, 229, 153))
}

.seafloorDepthLabel {
  font-size: 13px;
  font-weight: bold;
  color: rgb(117, 87, 0);
}

</style>
